import React, { useMemo } from 'react';
import { useRandomId } from '@tanium/react-use-random-id';
import { Id } from '../types/Id';
import { RibbonListProps } from '../types/StackedRibbonListComponentTypes';
import { RibbonListContext, RibbonListContextType } from './RibbonListContext';
import {
  RibbonListExpansionContext,
  RibbonListExpansionContextType,
} from './RibbonListExpansionContext';
import {
  RibbonListSelectionContext,
  RibbonListSelectionContextType,
} from './RibbonListSelectionContext';

interface Props<TId extends Id, TItem>
  extends Pick<
      RibbonListProps<TId, TItem>,
      | 'actions'
      | 'actionsVerticalAlignment'
      | 'checkboxVerticalAlignment'
      | 'columns'
      | 'dataGetter'
      | 'disclosureVerticalAlignment'
      | 'enableRowHighlight'
      | 'isIdEmphasized'
      | 'isItemLoaded'
      | 'isLoading'
      | 'isError'
      | 'isSelectionEnabled'
      | 'itemIdGetter'
      | 'selectionTooltipContent'
      | 'verticalAlignment'
      | 'onRowClick'
    >,
    RibbonListSelectionContextType<TId>,
    RibbonListExpansionContextType<TId, TItem> {
  children: React.ReactNode;
}

const RibbonListProviderInner = <TId extends Id, TItem>({
  actions,
  actionsVerticalAlignment,
  children,
  checkboxVerticalAlignment,
  columns,
  dataGetter,
  disclosureVerticalAlignment,
  enableRowHighlight,
  verticalAlignment,
  expandableContent,
  isExpandable,
  isIdEmphasized,
  isIdExpanded,
  isIdSelected,
  isLoading,
  isError,
  isSelectable,
  isSelectionEnabled,
  isItemLoaded,
  itemIdGetter,
  selectionTooltipContent,
  toggleExpansion,
  toggleSelection,
  hideExpansionDisclosure,
  onRowClick,
}: Props<TId, TItem>) => {
  const RibbonListContextProvider = RibbonListContext.Provider as unknown as React.Provider<
    RibbonListContextType<TId, TItem>
  >;
  const RibbonListExpansionContextProvider = RibbonListExpansionContext.Provider as React.Provider<
    RibbonListExpansionContextType<TId, TItem>
  >;
  const RibbonListSelectionContextProvider = RibbonListSelectionContext.Provider as React.Provider<
    RibbonListSelectionContextType<TId>
  >;
  const componentId = useRandomId();

  const ribbonListContextValue = useMemo(
    () => ({
      actions,
      actionsVerticalAlignment,
      checkboxVerticalAlignment,
      columns,
      dataGetter,
      disclosureVerticalAlignment,
      enableRowHighlight,
      isIdEmphasized,
      isItemLoaded,
      isLoading,
      isError,
      isSelectionEnabled,
      itemIdGetter,
      selectionTooltipContent,
      verticalAlignment,
      componentId,
      onRowClick,
    }),
    [
      actions,
      actionsVerticalAlignment,
      checkboxVerticalAlignment,
      columns,
      dataGetter,
      disclosureVerticalAlignment,
      enableRowHighlight,
      isIdEmphasized,
      isItemLoaded,
      isLoading,
      isError,
      isSelectionEnabled,
      itemIdGetter,
      selectionTooltipContent,
      verticalAlignment,
      componentId,
      onRowClick,
    ],
  );

  const RibbonListExpansionContextValue = useMemo(
    () => ({
      expandableContent,
      isExpandable,
      isIdExpanded,
      toggleExpansion,
      hideExpansionDisclosure,
    }),
    [expandableContent, isExpandable, isIdExpanded, toggleExpansion, hideExpansionDisclosure],
  );

  const ribbonListSelectionContextValue = useMemo(
    () => ({
      isIdSelected,
      isSelectable,
      toggleSelection,
    }),
    [isIdSelected, isSelectable, toggleSelection],
  );

  return (
    <RibbonListContextProvider value={ribbonListContextValue}>
      <RibbonListExpansionContextProvider value={RibbonListExpansionContextValue}>
        <RibbonListSelectionContextProvider value={ribbonListSelectionContextValue}>
          {children}
        </RibbonListSelectionContextProvider>
      </RibbonListExpansionContextProvider>
    </RibbonListContextProvider>
  );
};

export const RibbonListProvider = React.memo(RibbonListProviderInner) as <TId extends Id, TItem>(
  props: Props<TId, TItem>,
) => JSX.Element;
